import { motion } from 'framer-motion';
import { SectionMediaBackdrop } from './SectionMediaBackdrop';

/**
 * ProductionFlowSection — "Every order lands on the floor ready to build."
 *
 * Simple, centered, horizontal layout:
 *   eyebrow → centered headline → centered supporting copy
 *     → horizontal pipeline of 6 capability icons connected by a jade line
 *       → centered "See Production" link
 *
 * Desktop: all 6 icons in one row connected end-to-end.
 * Mobile: collapses to a 3×2 grid (no connecting line) so labels stay legible.
 */

function Icon({ children, className }) {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={1.5}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      className={className}
    >
      {children}
    </svg>
  );
}

const CutSheetIcon = (p) => (
  <Icon {...p}>
    <path d='M6 3h9l4 4v14H6z' />
    <path d='M15 3v4h4' />
    <path d='M9 12h7M9 15h7M9 18h4' />
  </Icon>
);

const StationIcon = (p) => (
  <Icon {...p}>
    <path d='M3 21h18' />
    <path d='M5 21V11l4 2V9l5 3v-3l5 3v9' />
    <rect x='9' y='15' width='2' height='3' />
    <rect x='15' y='15' width='2' height='3' />
  </Icon>
);

const InventoryIcon = (p) => (
  <Icon {...p}>
    <rect x='3' y='8' width='8' height='8' rx='1' />
    <rect x='13' y='3' width='8' height='8' rx='1' />
    <rect x='13' y='13' width='8' height='8' rx='1' />
  </Icon>
);

const ShippingIcon = (p) => (
  <Icon {...p}>
    <path d='M3 7h11v9H3z' />
    <path d='M14 10h4l3 3v3h-7' />
    <circle cx='7.5' cy='17.5' r='1.8' />
    <circle cx='17' cy='17.5' r='1.8' />
  </Icon>
);

const StationTrackingIcon = (p) => (
  <Icon {...p}>
    <circle cx='12' cy='12' r='3' />
    <path d='M12 2v3M12 19v3M2 12h3M19 12h3' />
    <path d='M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1' />
  </Icon>
);

const ReportingIcon = (p) => (
  <Icon {...p}>
    <path d='M18 20V10M12 20V4M6 20v-6' />
  </Icon>
);

const BottleneckIcon = (p) => (
  <Icon {...p}>
    <path d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' />
    <line x1='12' y1='9' x2='12' y2='13' />
    <line x1='12' y1='17' x2='12.01' y2='17' />
  </Icon>
);

// Capability cards shown below the headline — three production-visibility features.
const PRODUCTION_CAPABILITIES = [
  {
    key: 'station-tracking',
    Icon: StationTrackingIcon,
    title: 'Station tracking',
    body: 'See every order at every station in real time — router, sanding, finishing, assembly.',
  },
  {
    key: 'reporting',
    Icon: ReportingIcon,
    title: 'Reporting',
    body: 'Reports on throughput, on-time delivery, and cost per SKU.',
  },
  {
    key: 'bottleneck',
    Icon: BottleneckIcon,
    title: 'Bottleneck visibility',
    body: 'See where the bottlenecks are before they push a job late.',
  },
];

const CheckIcon = (p) => (
  <Icon {...p}>
    <path d='M5 12.5l4 4 10-10' />
  </Icon>
);

const ArrowRight = (p) => (
  <Icon {...p}>
    <path d='M5 12h14M13 6l6 6-6 6' />
  </Icon>
);

// 4-stage rail: customer order → built product.
// `Production` is the active stage so the section reads as a live system.
const FEATURES = [
  { key: 'cut', label: 'Cut Sheets', Icon: CutSheetIcon },
  { key: 'production', label: 'Production', Icon: StationIcon },
  { key: 'inventory', label: 'Inventory', Icon: InventoryIcon },
  { key: 'fulfillment', label: 'Fulfillment', Icon: ShippingIcon },
];
const ACTIVE_INDEX = 1; // Production

export function ProductionFlowSection() {
  return (
    <section
      id='production'
      className='relative overflow-hidden bg-zinc-950 py-14 md:py-20'
    >
      {/* CNC-cutting video backdrop (same source as the Industries band).
          intensity='heavy' applies strong scrims so the centered headline,
          copy, and pipeline stay readable on top of the moving footage. */}
      <SectionMediaBackdrop
        type='video'
        poster='/videos/cnc-cutting-poster.jpg'
        videoSources={[
          {
            src: '/videos/cnc-cutting-mobile.mp4',
            type: 'video/mp4',
            media: '(max-width: 640px)',
          },
          { src: '/videos/cnc-cutting.mp4', type: 'video/mp4' },
        ]}
        isDarkMode
        intensity='heavy'
        focal='object-[center_40%]'
      />
      {/* Extra dark overlay on top of the SectionMediaBackdrop's "heavy"
          scrim — knocks the CNC footage back further so the centered
          headline, pipeline visualization, and CTA button have enough
          contrast against the busy moving background. */}
      <div
        aria-hidden='true'
        className='absolute inset-0 pointer-events-none bg-zinc-950/35'
      />

      <div className='tc-container relative z-10'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className='text-center max-w-3xl mx-auto'
        >
          <span className='inline-block px-3 py-1 rounded-full text-[11px] font-sans tracking-[0.25em] uppercase border bg-jade-deep/15 text-jade-soft border-jade-deep/30 mb-5'>
            Production
          </span>
          <h2 className='text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] font-display text-white [text-wrap:balance]'>
            Every order lands on the floor{' '}
            <span className='text-jade-soft'>ready to build.</span>
          </h2>
          <p className='mt-4 text-base md:text-lg text-zinc-300 leading-relaxed'>
            Cut sheets, BOMs, part lists, and routing are generated from the
            order — with dimensions, materials, and specifications attached
            — so{' '}
            <span className='font-semibold text-jade-soft'>
              production doesn&rsquo;t have to reinterpret what sales already
              entered.
            </span>
          </p>
        </motion.div>

        {/* Production capability grid — station tracking, reporting, bottleneck visibility */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className='mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto'
        >
          {PRODUCTION_CAPABILITIES.map(({ key, Icon: CapIcon, title, body }) => (
            <div
              key={key}
              className='flex flex-col gap-2.5 rounded-xl bg-zinc-950/60 backdrop-blur-md ring-1 ring-white/[0.06] px-4 py-4'
            >
              <CapIcon className='h-5 w-5 text-jade-soft shrink-0' />
              <p className='text-sm font-semibold text-white leading-snug'>{title}</p>
              <p className='text-xs text-zinc-400 leading-relaxed'>{body}</p>
            </div>
          ))}
        </motion.div>

        {/* Horizontal pipeline */}
        <ProductionPipeline />

        {/* Centered CTA */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className='mt-10 md:mt-12 flex justify-center'
        >
          <a
            href='/product/production'
            className='group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-jade-soft text-zinc-950 text-sm md:text-base font-semibold hover:bg-jade transition-colors shadow-[0_8px_24px_-8px_rgba(52,211,153,0.45)]'
          >
            See Production
            <ArrowRight className='h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5' />
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/**
 * ProductionPipeline — 4-stage rail with one active stage + ambient
 * workflow artifact cards scattered around it.
 *
 * The rail goes from icon 0 to icon 3, with a dim jade gradient line
 * showing the full path. The segment from icon 0 to the active stage
 * is filled in solid jade — visually telling the eye where in the
 * pipeline this order currently sits.
 *
 * The active stage's icon ring is brighter and carries a pulsing live
 * dot. A status callout below ("Cutting · Station 3 of 6 · 75%") makes
 * the activity concrete.
 *
 * Surrounding the rail are 3 subtle artifact cards (barcode scan toast,
 * material reserved, QC pending) — these are the "operational depth"
 * Steph asked for: small moments of operational proof that suggest a
 * live system, not just a marketing diagram.
 */
function ProductionPipeline() {
  return (
    <div className='mt-12 md:mt-16 relative max-w-5xl mx-auto px-4'>
      {/* Single supporting artifact — materials reserved chip floating
          near the inventory column. Desktop only; mobile keeps the rail
          alone to stay legible. */}
      <div className='hidden lg:block'>
        <ArtifactCard
          position='absolute -top-6 right-0 lg:right-4'
          delay={0.7}
        >
          <MaterialReservedCard />
        </ArtifactCard>
      </div>

      {/* The rail. Single row of 4 on every breakpoint so the icons stay
          centered and the active callout has empty space below it.
          On mobile the icons shrink so 4 still fit at 320–375px without
          horizontal scroll. */}
      <div className='relative pb-16 md:pb-16'>
        {/* Base rail — hairline, low-opacity, shown across all sizes. */}
        <div
          aria-hidden='true'
          className='absolute top-6 md:top-7 h-px'
          style={{
            left: '12.5%',
            right: '12.5%',
            background: 'rgba(167, 243, 208, 0.12)',
          }}
        />
        {/* Active fill — from first stage's center to the active stage. */}
        <div
          aria-hidden='true'
          className='absolute top-[23px] md:top-[27px] h-[2px] origin-left'
          style={{
            left: '12.5%',
            width: `${((ACTIVE_INDEX + 0.5) / FEATURES.length) * 100 - 12.5}%`,
            background: 'rgba(110, 231, 183, 0.48)',
          }}
        />

        {/* Icon nodes — single row, 4 columns on every breakpoint */}
        <div className='relative grid grid-cols-4 gap-x-1 sm:gap-x-2'>
          {FEATURES.map(({ key, label, Icon: IconComp }, i) => {
            const isActive = i === ACTIVE_INDEX;
            const isPast = i < ACTIVE_INDEX;
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className='flex flex-col items-center text-center relative'
              >
                <div
                  className={`relative z-10 h-12 w-12 md:h-14 md:w-14 rounded-full backdrop-blur-md flex items-center justify-center transition-all duration-300 ${
                    isActive
                      ? 'bg-zinc-950/70 ring-1 ring-jade-soft/60'
                      : isPast
                        ? 'bg-zinc-950/70 ring-1 ring-jade-soft/30'
                        : 'bg-zinc-950/65 ring-1 ring-white/10'
                  }`}
                >
                  <IconComp
                    className={`h-5 w-5 md:h-6 md:w-6 ${
                      isActive
                        ? 'text-jade-soft'
                        : isPast
                          ? 'text-jade-soft/70'
                          : 'text-zinc-500'
                    }`}
                  />
                  {/* Live pulse dot for the active stage — small, no neon
                      shadow. The ping ring carries the "live" cue. */}
                  {isActive && (
                    <span
                      aria-hidden='true'
                      className='absolute -top-0.5 -right-0.5 inline-flex h-2.5 w-2.5'
                    >
                      <span className='absolute inset-0 rounded-full bg-jade-soft/40 animate-ping' />
                      <span className='relative inline-flex h-2.5 w-2.5 rounded-full bg-jade-soft/90 ring-2 ring-zinc-950' />
                    </span>
                  )}
                </div>
                <span
                  className={`mt-3 md:mt-3.5 text-[10px] md:text-[13px] font-semibold leading-[1.15] px-0.5 ${
                    isActive ? 'text-jade-soft' : 'text-white'
                  }`}
                >
                  {label}
                </span>

                {/* Active stage status callout — sits below the active label */}
                {isActive && <ActiveStageStatus />}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * ActiveStageStatus — small jade callout under the Production icon
 * showing the live operation ("Cutting · Station 3 of 6") with a
 * partial progress bar. Anchors the abstract pipeline in a concrete moment.
 */
function ActiveStageStatus() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, delay: 1.0 }}
      className='absolute top-[78px] md:top-[96px] left-1/2 -translate-x-1/2 w-max max-w-[180px] md:max-w-[200px] rounded-md bg-zinc-950/70 backdrop-blur-md ring-1 ring-jade-soft/15 px-2 md:px-2.5 py-1 md:py-1.5'
    >
      <div className='flex items-center gap-1.5'>
        <span className='relative inline-flex h-1.5 w-1.5 shrink-0'>
          <span className='absolute inset-0 rounded-full bg-jade-soft/30 animate-ping' />
          <span className='relative inline-flex h-1.5 w-1.5 rounded-full bg-jade-soft/90' />
        </span>
        <span className='text-[10px] md:text-[11px] font-semibold text-zinc-200 whitespace-nowrap'>
          Cutting · Station 3 of 6
        </span>
      </div>
      {/* Progress bar — flat fill, no glow. Industrial, not neon. */}
      <div className='mt-1.5 h-[2px] w-full rounded-full bg-[#ffffff]/[0.06] overflow-hidden'>
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: '75%' }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.2, delay: 1.2, ease: 'easeOut' }}
          className='h-full rounded-full bg-jade-soft/60'
        />
      </div>
    </motion.div>
  );
}

/**
 * ArtifactCard — wrapper for the floating workflow proof cards.
 * Handles entry animation and positioning so the child can focus on content.
 */
function ArtifactCard({ position, delay, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, delay }}
      className={`${position} z-20 pointer-events-none`}
    >
      {children}
    </motion.div>
  );
}

/** Material reserved card — small confirmation that materials are committed. */
function MaterialReservedCard() {
  return (
    <div className='inline-flex items-center gap-2 rounded-lg bg-jade-deep/15 backdrop-blur-md ring-1 ring-jade-soft/25 px-2.5 py-1.5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)]'>
      <CheckIcon className='h-3.5 w-3.5 text-jade-soft shrink-0' />
      <div className='flex flex-col leading-tight'>
        <span className='text-[8px] uppercase tracking-wider text-jade-soft/80'>
          Materials reserved
        </span>
        <span className='text-[10px] text-zinc-200'>
          18 of 18 items
        </span>
      </div>
    </div>
  );
}


export default ProductionFlowSection;
